import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Provider, useSelector } from 'react-redux'; // eslint-disable-line import/no-extraneous-dependencies
import i18n from 'i18next';
import backend from 'i18next-xhr-backend';
import { initReactI18next } from 'react-i18next';
import './initialiseModuleConfig';
import { Routes } from './Routes';
import { IStore, store } from './store/store';
import './bootstrap.css';
import { getConfig, FrontendContextProvider, initConfig, useLocalStorage, Themes, useZoom } from '@cmctechnology/webinvest-store-frontend';
import { webTradingApiInit } from '@cmctechnology/webinvest-api-client-web-trading';
import {
  EoraThemeProvider,
  ITheme,
  PhoenixModalProvider,
  ToastMessageProvider,
  cmcDarkTheme,
  cmcTheme,
  darkThemes,
  themes
} from '@cmctechnology/webinvest-design-system';
import { AppStage, APP_STAGE, getDocumentTheme, THEMES, STORAGE_KEYS, Theme } from '@cmctechnology/webinvest-constants';
import { MaintenanceProvider } from './hocs/MaintenanceContext';
import { CloudAnalytics } from './Modules';
import { AutoPrefixProvider } from './components/AutoPrefixProvider';
import { prefix } from 'stylis';
import { useRef } from 'react';
import styled from 'styled-components';

// TODO work around to get right version of stylis to load for autoprefixing
console.log(prefix.name);
const DEFAULT_LANGUAGE = 'en';
const i18nBackendLoadPath = `${process.env.PUBLIC_URL}/locales/{{lng}}/{{ns}}.json`;
i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(backend)
  .init({
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    nsSeparator: '::',
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    },
    backend: {
      loadPath: i18nBackendLoadPath
    }
  });

const appStage = (process.env.REACT_APP_STAGE as AppStage) ?? APP_STAGE.Dev;

initConfig(appStage);

webTradingApiInit(getConfig().stockbrokingApiUrl, {
  isDebug: appStage !== APP_STAGE.Prod
});

const PROD_THEMES: string[] = [THEMES.CMC, THEMES.CMCAlpha, THEMES.CMCSingapore, THEMES.ASB, THEMES.StGeorge, THEMES.Bendigo];
const NONPROD_THEMES: string[] = [THEMES.Advantage, THEMES.Rivkin, THEMES.eBroking, THEMES.Westpac];
// eslint-disable-next-line unicorn/prefer-set-has
const ENABLED_THEMES = [...PROD_THEMES, ...(appStage === APP_STAGE.Prod ? [] : NONPROD_THEMES)];

const ROOT_SELECTOR = '#root';

const container = document.querySelector(ROOT_SELECTOR);
const root = createRoot(container!);

const urlParams = new URLSearchParams(window.location.search);
const enableAsb = urlParams.get('enableAsbTheme') === 'true';
if (enableAsb) {
  localStorage.setItem('themeOverride', Themes.ASB);
}
const enableWestpac = urlParams.get('enableWestpacTheme') === 'true';
if (enableWestpac) {
  localStorage.setItem('themeOverride', Themes.Westpac);
}
// Keep existing theme in localStorage unless explicitly set by URL param
const disableThemeOverride = urlParams.get('disableThemeOverride') === 'true';
if (disableThemeOverride) {
  localStorage.removeItem('themeOverride');
}

const ContainerDiv = styled.div`
  & {
    input[data-autocompleted] {
      background-color: transparent !important;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:focus {
      transition:
        background-color 0s 0s,
        color 0s 0s;
      transition-delay: calc(infinity * 1s);
    }
  }
`;

const Application = () => {
  const containerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [isDarkMode] = useLocalStorage(STORAGE_KEYS.DarkMode, false);

  const themeOverride = localStorage.getItem('themeOverride') as Theme | null;
  const clientTheme = useSelector(({ client }: IStore) => client?.theme); // theme from client if authenticated
  const documentTheme = getDocumentTheme(document); // theme from route e.g. 'au', 'alpha', 'sg' or local storage theme setting (from previous auth)
  const theme = themeOverride ?? clientTheme ?? documentTheme ?? THEMES.CMC;

  let eoraTheme = themes[theme] || (cmcTheme as ITheme); // design system theme
  const { zoom, zoomStyle } = useZoom();

  if (isDarkMode && appStage !== APP_STAGE.Prod) {
    eoraTheme = darkThemes[theme] || cmcDarkTheme;
  }

  const isThemeEnabled = ENABLED_THEMES.includes(theme);

  if (!isThemeEnabled) {
    return <>{`Page is not available`}</>;
  }

  return (
    <ContainerDiv ref={containerRef} style={zoomStyle}>
      <FrontendContextProvider
        tradingRoutePrefix={`${process.env.PUBLIC_URL}/trading`}
        publicUrl={`${process.env.PUBLIC_URL}`}
        config={getConfig()}
        theme={theme}
        containerRef={containerRef}
      >
        <MaintenanceProvider>
          <EoraThemeProvider theme={eoraTheme!} currentEnv={getConfig().appStage} baseAssetsUrl={process.env.REACT_APP_BASE_ASSET_URL}>
            <AutoPrefixProvider>
              <ToastMessageProvider>
                <BrowserRouter>
                  <CloudAnalytics />
                  <PhoenixModalProvider zoom={zoom} />
                  <Routes />
                </BrowserRouter>
              </ToastMessageProvider>
            </AutoPrefixProvider>
          </EoraThemeProvider>
        </MaintenanceProvider>
      </FrontendContextProvider>
    </ContainerDiv>
  );
};

root.render(
  <Provider store={store}>
    <Application />
  </Provider>
);
